import { Link } from "@tanstack/react-router";
import { ArrowRight, Check, X } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const CLAY = "var(--color-ther-clay)";
const HAIRLINE = "oklch(88% 0.02 55)";
const MUTED = "oklch(45% 0.02 55)";

type T = (key: string) => string;

const WITH = [1, 2, 3, 4, 5].map((n) => `forTherapists.compare.with.p${n}`);
const ALONE = [1, 2, 3, 4, 5].map((n) => `forTherapists.compare.alone.c${n}`);

function WithCard({ t }: { t: T }) {
  return (
    <div
      className="relative flex h-full flex-col rounded-3xl bg-white p-7 shadow-[0_34px_70px_-34px_oklch(27%_0.045_42_/_0.4)] sm:p-9"
      style={{ border: "1px solid oklch(50% 0.13 40 / 0.16)" }}
    >
      <header className="mb-7">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: CLAY }}>
          {t("forTherapists.compare.with.label")}
        </span>
        <h3 className="font-heading mt-1.5 text-2xl font-black tracking-[-0.01em]" style={{ color: CLAY }}>
          {t("forTherapists.compare.with.name")}
        </h3>
      </header>
      <ul className="flex flex-col gap-4">
        {WITH.map((k) => (
          <li key={k} className="flex items-start gap-3">
            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full" style={{ background: CLAY }}>
              <Check className="size-3.5 text-white" weight="bold" aria-hidden />
            </span>
            <span className="text-[15px] font-medium leading-snug text-foreground sm:text-base">{t(k)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AloneCard({ t }: { t: T }) {
  return (
    <div className="relative flex h-full flex-col rounded-3xl p-7 sm:p-9" style={{ border: `1px solid ${HAIRLINE}` }}>
      <header className="mb-7">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: MUTED }}>
          {t("forTherapists.compare.alone.label")}
        </span>
        <h3 className="font-heading mt-1.5 text-2xl font-black tracking-[-0.01em]" style={{ color: MUTED }}>
          {t("forTherapists.compare.alone.name")}
        </h3>
      </header>
      <ul className="flex flex-col gap-4">
        {ALONE.map((k) => (
          <li key={k} className="flex items-start gap-3">
            <span
              className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full"
              style={{ background: "oklch(45% 0.02 55 / 0.1)" }}
            >
              <X className="size-3.5" weight="bold" style={{ color: MUTED }} aria-hidden />
            </span>
            <span className="text-[15px] font-medium leading-snug text-muted-foreground sm:text-base">{t(k)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Compare() {
  const ref = useScrollReveal<HTMLElement>();
  const { t } = useTranslation();

  return (
    <section
      ref={ref}
      className="animate-on-scroll relative overflow-hidden py-16 sm:py-24"
      style={{ background: "var(--color-ther-linen)" }}
    >
      <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2
            className="font-heading font-black leading-[1.05] tracking-[-0.02em] text-foreground"
            style={{ fontSize: "clamp(1.9rem, 4vw, 3rem)" }}
          >
            {t("forTherapists.compare.titlePrefix")}{" "}
            <em className="font-normal italic" style={{ color: CLAY }}>
              {t("forTherapists.compare.titleHighlight")}
            </em>
          </h2>
        </div>

        <div className="relative mt-12 grid grid-cols-1 gap-5 sm:mt-14 lg:grid-cols-2 lg:gap-6">
          <WithCard t={t} />
          <div className="flex items-center justify-center lg:pointer-events-none lg:absolute lg:inset-0 lg:z-20">
            <span
              className="font-heading flex size-12 items-center justify-center rounded-full bg-white text-sm font-black uppercase tracking-[0.06em] shadow-[0_12px_30px_-12px_oklch(27%_0.045_42_/_0.45)] sm:size-14"
              style={{ color: CLAY, border: `1px solid ${HAIRLINE}` }}
            >
              {t("forTherapists.compare.vs")}
            </span>
          </div>
          <AloneCard t={t} />
        </div>

        <div className="mt-10 flex justify-center sm:mt-12">
          <Link
            to="/therapist-enroll"
            className="group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{ background: CLAY }}
          >
            {t("forTherapists.compare.cta")}
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5" weight="bold" aria-hidden />
          </Link>
        </div>
      </div>
    </section>
  );
}
